import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Tilt from 'react-parallax-tilt';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import { usePageTracking } from '../hooks/useAnalytics';

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const Create = () => {
  usePageTracking('Create');
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);
  const { user, subscriptionStatus } = useAuth();
  const { apiClient } = useAPI();
  const [, setStoryCount] = useState(0);
  const [, setCharacterCount] = useState(0);

  useEffect(() => {
    loadCounts();
  }, [user, subscriptionStatus.subscriptionType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!apiClient || !user?.id) return;
    const handleCharacterDeleted = () => { loadCounts(); };
    window.addEventListener('character:deleted', handleCharacterDeleted);
    return () => window.removeEventListener('character:deleted', handleCharacterDeleted);
  }, [apiClient, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCounts = async () => {
    if (!apiClient || !user?.id) return;
    try {
      const [storiesResponse, charactersResponse] = await Promise.all([
        apiClient.getUserStories(),
        apiClient.getUserCharacters(user.id),
      ]);
      if (storiesResponse.status) setStoryCount(storiesResponse.data?.length || 0);
      if (charactersResponse.status) setCharacterCount(charactersResponse.data?.length || 0);
    } catch (error) {
      logger.error('Failed to load counts:', error);
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  const PaintBrushIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );
  const FilmIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 5v14M17 5v14M3 9h4M3 15h4M17 9h4M17 15h4" strokeLinecap="round" />
    </svg>
  );

  const renderCard = ({ id, accent, accentDeep, accentSoft, title, description, cta, media, onClick, IconComponent, custom }) => {
    const isHovered = hoveredCard === id;
    return (
      <motion.div
        custom={custom}
        initial="hidden"
        animate="visible"
        variants={cardVariants}
        onHoverStart={() => setHoveredCard(id)}
        onHoverEnd={() => setHoveredCard(null)}
      >
        <Tilt
          tiltMaxAngleX={6}
          tiltMaxAngleY={6}
          perspective={1200}
          scale={1.02}
          transitionSpeed={1500}
          gyroscope={false}
          className="tilt-card"
        >
          <div
            onClick={onClick}
            className="group relative cursor-pointer overflow-hidden flex flex-col items-center"
            style={{
              padding: 'clamp(28px, 4vh, 44px) 24px',
              gap: 'clamp(18px, 3vh, 28px)',
              background: '#FFFFFF',
              border: `2px solid ${isHovered ? accent : 'rgba(11,26,51,0.08)'}`,
              borderRadius: 28,
              boxShadow: isHovered
                ? `0px 6px 0px ${accentDeep}, 0px 18px 40px ${accentSoft}`
                : '0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)',
              transition: 'border-color 200ms, box-shadow 200ms, transform 200ms',
              transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
              fontFamily: "'Inter', 'Fredoka', sans-serif",
            }}
          >
            {/* Decorative blob behind icon */}
            <div
              style={{
                position: 'absolute',
                top: -80,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 320,
                height: 320,
                borderRadius: '50%',
                background: accentSoft,
                filter: 'blur(20px)',
                pointerEvents: 'none',
              }}
            />

            {/* Icon Tag */}
            <div className="relative">
              <div
                className="flex items-center gap-2 px-4 py-2"
                style={{
                  background: accent,
                  borderRadius: 999,
                  boxShadow: `0px 3px 0px ${accentDeep}`,
                }}
              >
                <IconComponent />
                <span
                  className="text-xs md:text-sm uppercase tracking-widest"
                  style={{ ...FREDOKA, color: '#FFFFFF', fontWeight: 800, letterSpacing: '0.10em' }}
                >
                  {id === 'character' ? 'Character' : 'Story'}
                </span>
              </div>
            </div>

            {/* Media */}
            <motion.div
              animate={isHovered ? { y: [-4, 4, -4], rotate: [0, 1.5, -1.5, 0] } : { y: 0, rotate: 0 }}
              transition={{ duration: 3, repeat: isHovered ? Infinity : 0 }}
              style={{
                width: 'clamp(160px, 20vw, 220px)',
                height: 'clamp(160px, 20vw, 220px)',
                background: `linear-gradient(135deg, ${accentSoft} 0%, rgba(255,255,255,0.6) 100%)`,
                border: `2px solid ${accent}`,
                borderRadius: '50%',
                overflow: 'hidden',
                boxShadow: `0px 6px 0px ${accentDeep}, 0px 18px 28px ${accentSoft}`,
              }}
              className="flex items-center justify-center"
            >
              {media}
            </motion.div>

            {/* Body */}
            <div className="relative flex flex-col items-center text-center" style={{ gap: 'clamp(10px, 1.5vh, 16px)' }}>
              <h2
                style={{
                  ...FREDOKA,
                  color: '#0B1A33',
                  fontWeight: 700,
                  fontSize: 'clamp(1.5rem, 2.6vw, 2.2rem)',
                  letterSpacing: '-0.01em',
                  textTransform: 'uppercase',
                  margin: 0,
                  lineHeight: 1.05,
                }}
              >
                {title}
              </h2>
              <p
                className="hidden md:block max-w-md"
                style={{ ...FREDOKA, color: '#5A6B85', fontSize: 15, lineHeight: 1.45, margin: 0 }}
              >
                {description}
              </p>

              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={(e) => { e.stopPropagation(); onClick(); }}
                className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-3.5"
                style={{
                  ...FREDOKA,
                  background: accent,
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: 15,
                  border: 'none',
                  borderRadius: 999,
                  boxShadow: `0px 4px 0px ${accentDeep}`,
                  cursor: 'pointer',
                  transition: 'transform 120ms, box-shadow 120ms',
                }}
              >
                <span>{cta}</span>
                <motion.svg
                  animate={isHovered ? { x: [0, 4, 0] } : { x: 0 }}
                  transition={{ duration: 1.2, repeat: isHovered ? Infinity : 0 }}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                </motion.svg>
              </motion.button>
            </div>
          </div>
        </Tilt>
      </motion.div>
    );
  };

  return (
    <div
      className="create-page-container min-h-screen relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      {/* Soft decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', top: -160, left: -160, width: 480, height: 480,
            borderRadius: '50%', background: 'rgba(0,101,248,0.07)', filter: 'blur(30px)',
          }}
        />
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', bottom: -200, right: -200, width: 520, height: 520,
            borderRadius: '50%', background: 'rgba(0,202,255,0.08)', filter: 'blur(30px)',
          }}
        />
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${(i * 83) % 100}%`,
              top: `${(i * 47) % 100}%`,
              width: 6, height: 6,
              background: 'rgba(0,101,248,0.18)',
            }}
            animate={{ y: [0, -60, 0], opacity: [0, 0.8, 0] }}
            transition={{ duration: 4 + (i % 3), repeat: Infinity, delay: i * 0.3 }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-40 pb-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-14"
        >
          <h1
            className="mb-3 md:mb-4"
            style={{
              ...FREDOKA,
              color: '#0B1A33',
              fontWeight: 700,
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              lineHeight: 1.0,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
            }}
          >
            Choose Your{' '}
            <span style={{ color: '#0065F8' }}>Creation Path</span>
          </h1>
          <p
            className="max-w-xl mx-auto"
            style={{ ...FREDOKA, color: '#5A6B85', fontSize: 'clamp(0.95rem, 1.3vw, 1.05rem)' }}
          >
            Bring a new character to life or turn your favorites into TV stars.
          </p>
        </motion.div>

        {/* Create Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 max-w-6xl mx-auto">
          {renderCard({
            id: 'character',
            custom: 0,
            accent: '#0065F8',
            accentDeep: '#073B8E',
            accentSoft: 'rgba(0,101,248,0.18)',
            title: 'Create Character',
            description: 'Draw or take a picture and create your next character!',
            cta: 'Start Creating',
            IconComponent: PaintBrushIcon,
            onClick: () => navigate('/character-lab'),
            media: (
              <img
                src="/assets/zippy-character.png"
                alt="Zippy the tiger"
                className="w-full h-full object-cover"
              />
            ),
          })}

          {renderCard({
            id: 'story',
            custom: 1,
            accent: '#00CAFF',
            accentDeep: '#0480A8',
            accentSoft: 'rgba(0,202,255,0.20)',
            title: 'Create Story',
            description: 'Turn your characters into TV stars and make your own shows!',
            cta: 'Start Writing',
            IconComponent: FilmIcon,
            onClick: () => navigate('/story-template'),
            media: (
              // Clip from "Rules Are Rules: Zippy's Flight Lesson" (Pixley Original)
              <video
                src="/assets/zippy-episode.mp4"
                muted
                loop
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
            ),
          })}
        </div>
      </div>
    </div>
  );
};

export default Create;
